// modules
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// hooks
import { useNewGroup } from "containers/NewGroup/useNewGroup";
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// redux
import {
	getGroupDetails,
	selectedGroupsDetailsSelector,
	resetSelectedGroupDetails,
	editGroup,
	resetEditGroup,
	toggleGroupActivation,
	hasUnaurizedErrorSelector,
	getGroupsList,
	loadingGroupPermissionSelector,
	getUserAuthDetails
} from "redux/userList/userSlice";

// components
import Modal from "components/molecules/ModalContainer/ModalContainer";
import CountDownDialog from "components/molecules/ModalContainer/CountDownModalContainer";

// constants
import { EditGroup, NewGroup, Routes, USER_STATE, ViewMode } from "constants/global";

// utils
import { invalidateCookie } from "utils/token";

export type LocationState = {
	state: { group_code: string };
	pathname: string;
};

export function useEditGroup() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const location = useLocation();

	const { state, pathname } = location as LocationState;

	const {
		fieldErrors,
		validateFields,
		groupDetails,
		permissionList,
		selectedRoles,
		loading,
		isReimbVoucherGrpSelected,
		isDeclarationVerificationSelected,
		onCreateGroupFailure,
		setSelectedRoles,
		handleOnPermissionSelect,
		handleGroupDetailsChange
	} = useNewGroup();

	const [mode, setMode] = useState<string>(ViewMode.READ);

	const selectedGroupDetails = useAppSelector(selectedGroupsDetailsSelector);
	const { data, errors } = selectedGroupDetails || {};
	const hasUnaurizedError = useAppSelector(hasUnaurizedErrorSelector);
	const dataLoading = useAppSelector(loadingGroupPermissionSelector);

	const initialDetails = data && data[0];

	const initialFieldValues = useMemo(() => {
		if (data && data[0]) {
			const group = data[0];
			return {
				name: group?.group_code,
				description: group?.group_desc,
				selectedRoles: group?.permissions
			};
		}

		return {
			name: "",
			description: "",
			selectedRoles: []
		};
	}, [data]);

	const isGroupActive = initialDetails?.active === USER_STATE.enabled;

	useEffect(() => {
		if (state?.group_code && pathname?.includes("edit-group")) {
			dispatch(getGroupDetails({ group_code: state?.group_code }));
		}

		return () => {
			dispatch(resetEditGroup());
			dispatch(resetSelectedGroupDetails());
		};
	}, []);

	// Update mode from read to edit
	const handleMode = useCallback(
		(modeValue: string) => {
			setMode(modeValue);
		},
		[setMode]
	);

	// Navigates to role management
	const handleGoBack = useCallback(() => {
		navigate(Routes.ROLE_MANAGEMENT);
		dispatch(getUserAuthDetails());
	}, [navigate, dispatch, getUserAuthDetails]);

	const onFailure = useCallback(() => {
		navigate(`${Routes.ROLE_MANAGEMENT}/${Routes.EDIT_GROUP}`);
	}, [navigate]);

	const navigateToLogin = useCallback(() => {
		invalidateCookie();
		navigate(Routes?.LOGIN, { state: { path: Routes.ROLE_MANAGEMENT } });
	}, [navigate]);

	const toggleActivationSuccess = useCallback(
		(group_id: string | undefined, hasError: boolean, message: string | undefined) => {
			if (group_id && hasError && message) {
				const titleMessage = message === EditGroup.ACTIVATE_RESP_MESSAGE ? EditGroup.ACTIVATE_SUCCESS_MESSAGE : EditGroup.DEACTIVATE_SUCCESS_MESSAGE;
				CountDownDialog({
					timer: 3,
					onTimeComplete: handleGoBack,
					title: titleMessage
				});
				dispatch(getGroupsList({}));
			}
		},
		[CountDownDialog, handleGoBack]
	);

	const toggleActivation = useCallback(async () => {
		const payload = {
			group_id: initialDetails?.group_id as string,
			is_activate: !isGroupActive
		};

		try {
			const { data: toggleGroupData, errors: toggleGroupError, message } = await dispatch(toggleGroupActivation(payload)).unwrap();

			toggleActivationSuccess(toggleGroupData?.group_id, !toggleGroupError?.length, message);
		} catch (error) {
			// axios error
			const err = error as any;
			if (err?.response?.status === 401) {
				navigateToLogin();
			}
		}
	}, [isGroupActive, initialDetails, toggleGroupActivation, dispatch, CountDownDialog, handleGoBack]);

	const handleDeactivateGroup = useCallback(() => {
		Modal({
			type: "confirm",
			closable: true,
			title: EditGroup.WARNING_MESSAGE,
			actionButton: {
				label: EditGroup.DEACTIVATE_LABEL,
				onClick: toggleActivation
			},
			cancelButton: {
				label: EditGroup.GO_BACK,
				onClick: handleGoBack
			}
		});
	}, [Modal, toggleActivation, handleGoBack]);

	// Check whether user details are updated
	const isDetailsUpdated = useMemo(() => {
		const errs = Object.values(fieldErrors)?.filter((v) => !!v);

		if (errs?.length) {
			return false;
		}

		const values = Object.values(groupDetails).filter((v) => !!v);

		if (!selectedRoles?.length) {
			return false;
		}

		if (values?.length || selectedRoles?.length !== initialDetails?.permissions?.length) {
			return true;
		}

		// If length are same but permissions are different
		const len = selectedRoles?.length;
		for (let i = 0; i < len; i++) {
			if (!initialDetails?.permissions?.includes(selectedRoles[i])) {
				return true;
			}
		}

		return false;
	}, [groupDetails, selectedRoles, initialDetails, fieldErrors]);

	const submitChanges = useCallback(async () => {
		if (isDeclarationVerificationSelected && !isReimbVoucherGrpSelected) {
			Modal({
				title: NewGroup.DECLARE_VERIFY_MESSAGE,
				type: "confirm",
				closable: true,
				cancelButton: {
					label: "Ok",
					classes: {
						label: "back_button_label_white",
						buttonInput: "back_button_violet"
					}
				}
			});
			return;
		}
		const isFieldsValid = isDetailsUpdated && validateFields(false);

		if (isDetailsUpdated && isFieldsValid) {
			const payload = {
				group_id: `${initialDetails?.group_id}`,
				group_code: `${groupDetails?.name || initialDetails?.group_code}`,
				group_desc: `${groupDetails?.description || initialDetails?.group_desc}`,
				permissions: selectedRoles
			};

			try {
				const { data: editGroupData, errors: editGroupErrors, message } = await dispatch(editGroup(payload)).unwrap();

				if (editGroupData?.group_id && !editGroupErrors?.length && message === EditGroup.EDIT_RESP_MESSAGE) {
					CountDownDialog({
						timer: 3,
						onTimeComplete: handleGoBack,
						title: EditGroup?.SUCCESS_MESSAGE
					});
					dispatch(getGroupsList({}));
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigateToLogin();
				}
				onCreateGroupFailure(err?.response?.data?.errors[0]?.message, onFailure);
			}
		}
	}, [groupDetails, selectedRoles, isDetailsUpdated, editGroup, CountDownDialog, handleGoBack]);

	useMemo(() => {
		if (initialDetails?.permissions && initialDetails?.permissions?.length) {
			setSelectedRoles(initialDetails?.permissions);
		}
	}, [initialDetails, setSelectedRoles]);

	useEffect(() => {
		if (hasUnaurizedError) {
			// should comeback to role-management to avoid data loss.
			// e.g. we need group_code to fetch data onload that may not be available when navigating from login.
			navigateToLogin();
		}
	}, [hasUnaurizedError]);

	return {
		mode,
		groupDetails,
		permissionList,
		selectedRoles,
		isDetailsUpdated,
		initialValues: initialFieldValues,
		fieldErrors,
		isGroupActive,
		loading: loading || (!data && !errors?.length),
		btnLoading: dataLoading === 1,
		toggleActivation,
		validateFields,
		handleMode,
		handleOnPermissionSelect,
		handleGoBack,
		submitChanges,
		handleDeactivateGroup,
		handleGroupDetailsChange
	};
}
